import { set, sumDateFrom, sumDatePreset, sumDateTo, tickets } from '../state';
import { esc } from './ui';

function parseTicketDate(str: string): Date | null {
  if (!str) return null;
  const m = str.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) return null;
  const y = parseInt(m[3]!) > 2400 ? parseInt(m[3]!) - 543 : parseInt(m[3]!);
  return new Date(y, parseInt(m[2]!) - 1, parseInt(m[1]!));
}

function getFilteredTickets() {
  if (!sumDateFrom && !sumDateTo) return tickets;
  return tickets.filter(t => {
    const d = parseTicketDate(t.createdAt);
    if (!d) return true;
    if (sumDateFrom && d < sumDateFrom) return false;
    if (sumDateTo   && d > sumDateTo)   return false;
    return true;
  });
}

function pct(n: number, d: number): number {
  return d ? Math.round((n / d) * 100) : 0;
}

export function setDateRange(preset: string): void {
  set.sumDatePreset(preset);
  document.querySelectorAll('.date-preset').forEach(b =>
    b.classList.toggle('active', (b as HTMLElement).dataset['r'] === preset));
  const now = new Date(), today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'today') {
    set.sumDateFrom(today); set.sumDateTo(new Date(today.getTime() + 86399999));
  } else if (preset === 'week') {
    const mon = new Date(today); mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    set.sumDateFrom(mon); set.sumDateTo(new Date(mon.getTime() + 6 * 86400000 + 86399999));
  } else if (preset === 'month') {
    set.sumDateFrom(new Date(now.getFullYear(), now.getMonth(), 1));
    set.sumDateTo(new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59));
  } else if (preset === 'year') {
    set.sumDateFrom(new Date(now.getFullYear(), 0, 1));
    set.sumDateTo(new Date(now.getFullYear(), 11, 31, 23, 59, 59));
  } else if (preset === 'custom') {
    const f = (document.getElementById('dateFrom') as HTMLInputElement).value;
    const t = (document.getElementById('dateTo') as HTMLInputElement).value;
    set.sumDateFrom(f ? new Date(f) : null);
    set.sumDateTo(t ? new Date(t + 'T23:59:59') : null);
  } else {
    set.sumDateFrom(null); set.sumDateTo(null);
  }
  renderSummary();
}
void sumDatePreset; // exported state, referenced via import

export function renderSummary(): void {
  const t = getFilteredTickets(), total = t.length || 1;
  const nNew  = t.filter(x => x.status === 'new').length;
  const nProc = t.filter(x => x.status === 'processing').length;
  const nDone = t.filter(x => x.status === 'done').length;
  document.getElementById('sk-total')!.textContent = String(t.length);
  document.getElementById('sk-new')!.textContent   = String(nNew);
  document.getElementById('sk-proc')!.textContent  = String(nProc);
  document.getElementById('sk-done')!.textContent  = String(nDone);

  const topicMap: Record<string, { total: number; new: number; processing: number; done: number }> = {};
  t.forEach(x => {
    if (!topicMap[x.topic]) topicMap[x.topic] = { total: 0, new: 0, processing: 0, done: 0 };
    topicMap[x.topic]!.total++;
    (topicMap[x.topic]! as Record<string, number>)[x.status] = ((topicMap[x.topic]! as Record<string, number>)[x.status] ?? 0) + 1;
  });
  const topics = Object.entries(topicMap).sort((a, b) => b[1].total - a[1].total);
  document.getElementById('sumTopicList')!.innerHTML = topics.length ? topics.map(([name, m]) => {
    const doneW = pct(m.done, m.total), procW = pct(m.processing, m.total), newW = 100 - doneW - procW;
    return `<div class="topic-row">
      <div><div style="font-size:13px;font-weight:600">${esc(name)}</div><div style="font-size:11px;color:var(--muted)">${m.total} เรื่อง</div></div>
      <div>
        <div class="stacked-bar">
          ${doneW > 0 ? `<div class="stacked-seg" style="width:${doneW}%;background:#22C55E"></div>` : ''}
          ${procW > 0 ? `<div class="stacked-seg" style="width:${procW}%;background:#F97316"></div>` : ''}
          ${newW  > 0 ? `<div class="stacked-seg" style="width:${newW}%;background:#3B82F6"></div>`  : ''}
        </div>
        <div style="display:flex;gap:10px;margin-top:4px;font-size:10px">
          <span style="color:#22C55E">เสร็จ ${m.done}</span>
          <span style="color:#F97316">ดำเนิน ${m.processing}</span>
          <span style="color:#3B82F6">ใหม่ ${m.new}</span>
        </div>
      </div>
      <div style="text-align:right;font-size:12px;font-weight:700;color:var(--primary)">${pct(m.total, total)}%</div>
    </div>`;
  }).join('') : '<div class="empty">ยังไม่มีข้อมูล</div>';

  const hrMap: Record<string, number> = {};
  t.forEach(x => { if (x.hrName) { hrMap[x.hrName] = (hrMap[x.hrName] ?? 0) + 1; } });
  const hrs = Object.entries(hrMap).sort((a, b) => b[1] - a[1]), maxHR = hrs[0]?.[1] ?? 1;
  document.getElementById('sumHRList')!.innerHTML = hrs.length ? hrs.map(([name, count]) => `
    <div class="hr-row">
      <div class="hr-avatar"><i class="ti ti-user"></i></div>
      <div class="hr-bar-wrap">
        <div style="display:flex;justify-content:space-between">
          <span style="font-size:12px;font-weight:600">${esc(name)}</span>
          <span style="font-size:12px;color:var(--muted)">${count} เรื่อง</span>
        </div>
        <div class="hr-bar-bg"><div class="hr-bar-fill" style="width:${pct(count, maxHR)}%"></div></div>
      </div>
    </div>`).join('') : '<div class="empty" style="padding:16px">ยังไม่มีข้อมูล</div>';

  const priData = [
    { key: 'high',   label: 'ด่วนมาก',     color: '#DC2626', bg: '#FEE2E2' },
    { key: 'medium', label: 'ปกติ',         color: '#D97706', bg: '#FEF3C7' },
    { key: 'low',    label: 'ไม่เร่งด่วน', color: '#6B7280', bg: '#F3F4F6' },
  ];
  const priCount: Record<string, number> = { high: 0, medium: 0, low: 0 };
  t.forEach(x => priCount[x.priority ?? 'medium']!++);
  document.getElementById('sumPriList')!.innerHTML = priData.map(p => `
    <div class="pri-row">
      <span class="pill" style="background:${p.bg};color:${p.color};font-size:10px;min-width:80px;justify-content:center">${p.label}</span>
      <div class="pri-bar-wrap"><div class="pri-bar-fill" style="width:${pct(priCount[p.key]!, total)}%;background:${p.color}"></div></div>
      <span style="font-size:12px;font-weight:600;color:${p.color};min-width:52px;text-align:right">${priCount[p.key]} (${pct(priCount[p.key]!, total)}%)</span>
    </div>`).join('');
}

export function exportSummaryCSV(): void {
  const filtered = getFilteredTickets();
  const rows: string[][] = [['Ticket ID', 'หัวข้อ', 'ผู้แจ้ง', 'สาขา', 'สถานะ', 'HR ผู้ดูแล', 'Priority', 'สร้างเมื่อ']];
  filtered.forEach(t => {
    const { empName, branch } = { empName: t.empName, branch: '' };
    const parts = t.empName.split(/[|/]/).map(s => s.trim());
    rows.push([t.id, t.topic, parts[1] ?? empName, parts[2] ?? branch, t.status, t.hrName ?? '', t.priority ?? 'medium', t.createdAt]);
  });
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `HR_Summary_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}
